/**
 * DS_10 — cross-sell quick-view bottom sheet (mobile <= 767px) for the DCOP +
 * OrthoCurve PDPs. Theme-native port of the won Intelligems variant. One panel
 * per cross-sell product is server-rendered in snippets/ds10-cross-sell.liquid
 * (variants JSON, images, description tab — no runtime page scraping); this
 * class opens/closes the sheet, drives the option dropdowns / variant matching
 * / qty stepper, and adds to cart.
 *
 * Deviations from the won code (all intentional, see build brief):
 *   - Add to cart dispatches the theme's own `ajaxProduct:added` event, which
 *     rebuilds AND opens the cart drawer in one step (theme.js). The won code
 *     used `cart:build` + clicking #HeaderCartTrigger. The sheet always closes
 *     BEFORE the event fires — the drawer lives in the sticky header at
 *     z-index ~10 and would otherwise be hidden behind this sheet (z 9600).
 *     The main product form (form.product-single__form) is never touched.
 *   - On a failed add the sheet STAYS OPEN and the error is logged (the won
 *     code silently closed it, losing the shopper's selection).
 *   - Scroll lock mirrors DS_06's sole-owner pattern (js-ds10-locked +
 *     --ds10-scroll-y) instead of the won body.ds_10_bottomSheet_active class.
 *   - The wrapper is moved to <body> on init (DS_06 pattern) so position:fixed
 *     cannot be trapped by a transformed ancestor inside the product section.
 */

const MOBILE_MAX_WIDTH = 767;
const SCROLL_LOCK_CLASS = 'js-ds10-locked';
const SHEET_ACTIVE_CLASS = 'ds10-sheet--active';
const PANEL_ACTIVE_CLASS = 'ds10-sheet__product--active';
const TAB_OPEN_CLASS = 'ds10-sheet__tab-btn--open';
const DROPDOWN_OPEN_CLASS = 'ds10-sheet__dropdown--open';
const VALUE_SELECTED_CLASS = 'ds10-sheet__value--selected';
const ATC_SOLDOUT_CLASS = 'ds10-sheet__atc--soldout';
const BTN_LOADING_CLASS = 'btn--loading';

class Ds10Sheet {
  constructor() {
    this.root = document.querySelector('[data-ds10-sheet]');
    this.ownsLock = false;
    this.busy = false;
    this.slider = null;
    if (this.root) {
      this.init();
    }
  }

  init() {
    // clientWidth, not innerWidth: it is what the (width <= 767px) CSS media
    // query matches, and unlike innerWidth it is not skewed by scrollbars or
    // zoom (observed: a sibling handler's Flickity init shifted innerWidth
    // mid-DOMContentLoaded while clientWidth held steady).
    if (document.documentElement.clientWidth > MOBILE_MAX_WIDTH) {
      return;
    }
    document.body.appendChild(this.root);
    this.root.style.removeProperty('display'); // CSS owns visibility from here

    document.addEventListener('ds10:openSheet', (event) => {
      this.open(event.detail.handle);
    });
    this.root.addEventListener('click', (event) => {
      this.onClick(event);
    });
  }

  open(handle) {
    const panel = this.root.querySelector(`[data-ds10-panel="${handle}"]`);
    if (!panel) {
      return;
    }
    const previous = this.root.querySelector(`.${PANEL_ACTIVE_CLASS}`);
    if (previous) {
      previous.classList.remove(PANEL_ACTIVE_CLASS);
    }
    panel.classList.add(PANEL_ACTIVE_CLASS);
    this.resetPanel(panel);
    this.lockScroll();
    this.root.classList.add(SHEET_ACTIVE_CLASS);
    this.root.querySelector('.ds10-sheet__container').scrollTop = 0;
    this.initSlider(panel);
  }

  close() {
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
    this.root.classList.remove(SHEET_ACTIVE_CLASS);
    this.unlockScroll();
  }

  /**
   * The won sheet was rebuilt from scratch on every open; our panels persist,
   * so re-opening resets them to the same fresh state: qty 1, first available
   * variant selected, tabs and dropdowns closed.
   */
  resetPanel(panel) {
    const qtyValue = panel.querySelector('[data-ds10-qty-value]');
    if (qtyValue) {
      qtyValue.textContent = '1';
    }
    panel.querySelectorAll(`.${TAB_OPEN_CLASS}`).forEach((btn) => {
      btn.classList.remove(TAB_OPEN_CLASS);
      btn.setAttribute('aria-expanded', 'false');
    });
    panel.querySelectorAll(`.${DROPDOWN_OPEN_CLASS}`).forEach((dropdown) => {
      dropdown.classList.remove(DROPDOWN_OPEN_CLASS);
    });
    panel.querySelectorAll('[data-ds10-option]').forEach((option) => {
      const defaultValue = option.querySelector('[data-ds10-default]');
      if (!defaultValue) {
        return;
      }
      const current = option.querySelector(`.${VALUE_SELECTED_CLASS}`);
      if (current) {
        current.classList.remove(VALUE_SELECTED_CLASS);
      }
      defaultValue.classList.add(VALUE_SELECTED_CLASS);
      option.querySelector('[data-ds10-select-text]').textContent =
        defaultValue.getAttribute('data-value');
    });
    this.updateVariant(panel);
  }

  initSlider(panel) {
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
    const sliderEl = panel.querySelector('[data-ds10-slider]');
    if (!sliderEl || !sliderEl.children.length || !window.Flickity) {
      return;
    }
    this.slider = new window.Flickity(sliderEl, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      draggable: true,
    });
  }

  onClick(event) {
    const target = event.target;
    const panel = this.root.querySelector(`.${PANEL_ACTIVE_CLASS}`);

    // Any tap outside an option control closes the open dropdown (won behavior).
    if (!target.closest('[data-ds10-option]')) {
      this.closeDropdowns();
    }

    if (target.closest('[data-ds10-close]')) {
      this.close();
      return;
    }
    if (!panel) {
      return;
    }

    const tabBtn = target.closest('[data-ds10-tab-btn]');
    if (tabBtn) {
      this.toggleTab(panel, tabBtn);
      return;
    }
    const select = target.closest('[data-ds10-select]');
    if (select) {
      this.toggleDropdown(select);
      return;
    }
    const value = target.closest('[data-ds10-value]');
    if (value) {
      this.selectValue(panel, value);
      return;
    }
    if (target.closest('[data-ds10-qty-minus]') || target.closest('[data-ds10-qty-plus]')) {
      this.stepQty(panel, Boolean(target.closest('[data-ds10-qty-plus]')));
      return;
    }
    if (target.closest('[data-ds10-atc-btn]')) {
      this.addToCart(panel);
    }
  }

  toggleTab(panel, tabBtn) {
    const wasOpen = tabBtn.classList.contains(TAB_OPEN_CLASS);
    panel.querySelectorAll(`.${TAB_OPEN_CLASS}`).forEach((btn) => {
      btn.classList.remove(TAB_OPEN_CLASS);
      btn.setAttribute('aria-expanded', 'false');
    });
    if (!wasOpen) {
      tabBtn.classList.add(TAB_OPEN_CLASS);
      tabBtn.setAttribute('aria-expanded', 'true');
    }
  }

  toggleDropdown(select) {
    const dropdown = select.closest('[data-ds10-option]').querySelector('[data-ds10-dropdown]');
    const wasOpen = dropdown.classList.contains(DROPDOWN_OPEN_CLASS);
    this.closeDropdowns();
    if (!wasOpen) {
      dropdown.classList.add(DROPDOWN_OPEN_CLASS);
    }
  }

  closeDropdowns() {
    this.root.querySelectorAll(`.${DROPDOWN_OPEN_CLASS}`).forEach((dropdown) => {
      dropdown.classList.remove(DROPDOWN_OPEN_CLASS);
    });
  }

  selectValue(panel, value) {
    const option = value.closest('[data-ds10-option]');
    const current = option.querySelector(`.${VALUE_SELECTED_CLASS}`);
    if (current) {
      current.classList.remove(VALUE_SELECTED_CLASS);
    }
    value.classList.add(VALUE_SELECTED_CLASS);
    option.querySelector('[data-ds10-select-text]').textContent = value.getAttribute('data-value');
    this.closeDropdowns();
    this.updateVariant(panel);
  }

  stepQty(panel, isPlus) {
    const qtyValue = panel.querySelector('[data-ds10-qty-value]');
    const current = Number(qtyValue.textContent.trim()) || 1;
    const next = isPlus ? current + 1 : Math.max(1, current - 1);
    qtyValue.textContent = `${next}`;
  }

  /**
   * Match the selected option values (DOM order = option1..option3, hidden
   * single-value option3 included) against the server-rendered variants JSON —
   * the exact data the won code scraped out of the product page's
   * `{{ product.variants | json }}` textarea.
   */
  updateVariant(panel) {
    const variants = JSON.parse(panel.querySelector('[data-ds10-variants]').textContent);
    const selected = [...panel.querySelectorAll(`.${VALUE_SELECTED_CLASS}`)].map((el) =>
      el.getAttribute('data-value')
    );
    const variant = variants.find((candidate) =>
      candidate.options.every((optionValue, index) => optionValue === selected[index])
    );
    const atc = panel.querySelector('[data-ds10-atc]');

    if (!variant) {
      atc.classList.add(ATC_SOLDOUT_CLASS);
      return;
    }
    atc.setAttribute('data-variant-id', `${variant.id}`);
    atc.classList.toggle(ATC_SOLDOUT_CLASS, !variant.available);
    // innerHTML, not textContent: the shop's money format wraps amounts in
    // <span class=money>, and formatMoney reproduces that markup.
    const price = this.formatMoney(variant.price, variant.id);
    panel.querySelector('[data-ds10-price]').innerHTML = price;
    panel.querySelector('[data-ds10-atc-price]').innerHTML = price;
  }

  formatMoney(cents, variantId) {
    // Same lookup theme.js does for its own variant prices: an active
    // Intelligems price test overrides the Liquid-rendered amount.
    let amount = cents;
    if (window.igData && window.igData.price && window.igData.price.getPriceByVariantId) {
      amount = window.igData.price.getPriceByVariantId(variantId) || cents;
    }
    if (window.theme && window.theme.Currency && window.theme.settings) {
      return window.theme.Currency.formatMoney(amount, window.theme.settings.moneyFormat);
    }
    return `$${(amount / 100).toFixed(2)}`;
  }

  addToCart(panel) {
    if (this.busy) {
      return;
    }
    const atc = panel.querySelector('[data-ds10-atc]');
    if (atc.classList.contains(ATC_SOLDOUT_CLASS)) {
      return;
    }
    const btn = panel.querySelector('[data-ds10-atc-btn]');
    const variantId = Number(atc.getAttribute('data-variant-id'));
    const quantity = Number(panel.querySelector('[data-ds10-qty-value]').textContent.trim()) || 1;

    this.busy = true;
    btn.classList.add(BTN_LOADING_CLASS);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`[DS_10] add to cart failed (${response.status})`);
        }
        return response.json();
      })
      .then((item) => {
        this.busy = false;
        btn.classList.remove(BTN_LOADING_CLASS);
        const handle = panel.getAttribute('data-ds10-panel');
        // Close FIRST: the cart drawer (sticky header, z ~10) opens on the
        // event below and must not end up behind this sheet.
        this.close();
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: item, addToCartBtn: btn },
        }));
        document.dispatchEvent(new CustomEvent('ds10:productAdded', {
          detail: { handle },
        }));
      })
      .catch((error) => {
        this.busy = false;
        btn.classList.remove(BTN_LOADING_CLASS);
        console.error(error);
      });
  }

  /* ── Body scroll lock — DS_06's sole-owner pattern with DS_10's own class ── */

  lockScroll() {
    if (document.body.classList.contains(SCROLL_LOCK_CLASS)) {
      return;
    }
    document.body.style.setProperty('--ds10-scroll-y', `-${window.scrollY}px`);
    document.body.classList.add(SCROLL_LOCK_CLASS);
    this.ownsLock = true;
  }

  unlockScroll() {
    if (!this.ownsLock) {
      return;
    }
    const offset = parseInt(document.body.style.getPropertyValue('--ds10-scroll-y'), 10);
    const restoreTop = Number.isNaN(offset) ? 0 : -offset;
    document.body.classList.remove(SCROLL_LOCK_CLASS);
    document.body.style.removeProperty('--ds10-scroll-y');
    window.scrollTo({ top: restoreTop, behavior: 'instant' });
    this.ownsLock = false;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Ds10Sheet(); // eslint-disable-line no-new
});
